import { useEffect, useRef } from 'react';
import { useTrack } from '../useTrack';
import { getLogger } from '../../libs/logger';
import { TrackedEvents } from '../../features/tracking';

type TrackingData = {
  [key: string]: number;
} & {
  test_patient_added_at?: number;
  test_call_started_at?: number;
  call_duration_in_ms?: number;
  waiting_duration_in_ms?: number;
};

const logger = getLogger('UserTestPatientTracking');

/**
 * We need to collection as much information as possible about the test call experience.
 * Basically all the clicks the provider does in the test call.
 * Also we need to know the duration of the Test Patient in the queue and the duration of the call itself.
 */
export const useTestPatientTracking = (isTrackingEnabled: boolean = false) => {
  const trackingData = useRef<TrackingData>({});

  const track = useTrack();

  useEffect(() => {
    if (!isTrackingEnabled) {
      if (trackingData.current.test_patient_added_at) {
        onEvent('test_patient_removed');
      }
      return;
    }
    onEvent('add_test_patient');
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Ensure target is an HTMLElement before calling closest
      if (!target || !target.closest) return;
      // Find first parent with data-test-patient-click-tracking attribute (including the element itself)
      const elementWithTracking = target.closest(
        '[data-test-patient-click-tracking]',
      );

      const testPatientClickEvent = elementWithTracking?.getAttribute(
        'data-test-patient-click-tracking',
      );

      if (testPatientClickEvent) {
        onEvent(testPatientClickEvent);
      }
    };

    document.body.addEventListener('click', handleClick);
    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, [isTrackingEnabled]);

  const onEvent = (eventName: string) => {
    const prefixedEventName = `clicked_${eventName}`;
    logger.info(eventName, trackingData.current);
    switch (eventName) {
      case 'add_test_patient':
        trackingData.current.test_patient_added_at = new Date().getTime();
        track(TrackedEvents.TestPatientAdded, trackingData.current);
        logger.info(TrackedEvents.TestPatientAdded, trackingData.current);

        break;
      case 'start_call_button':
        trackingData.current.test_call_started_at = new Date().getTime();
        trackingData.current.waiting_duration_in_ms =
          new Date().getTime() - trackingData.current.test_patient_added_at!;
        track(TrackedEvents.StartTestCall, trackingData.current);
        logger.info(TrackedEvents.StartTestCall, trackingData.current);

        break;
      case 'test_patient_removed':
        trackingData.current.call_duration_in_ms =
          new Date().getTime() - trackingData.current.test_call_started_at!;
        track(TrackedEvents.EndTestCall, trackingData.current);

        logger.info(TrackedEvents.EndTestCall, trackingData.current);

        trackingData.current = {};
        break;
      default:
        trackingData.current[prefixedEventName] =
          (trackingData.current[prefixedEventName] || 0) + 1;
        break;
    }
  };
};
